import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactNode,
  RefAttributes,
} from 'react';

import { Typography } from '../typography';

import styles from './alert.scss';

type Props = RefAttributes<HTMLDivElement> &
  Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'content'> & {
    open: boolean;
    title?: string | ReactNode;
    content?: string | ReactNode;
    vertical?: boolean;
    dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-alert';

export const Alert: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      open = false,
      title,
      content,
      vertical = false,
      children,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    if (!open) return null;

    return (
      <div
        data-id={dataTestId}
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        className={classNames(className, styles.backdrop)}
        ref={ref}
        {...rest}>
        <div className={styles.root}>
          <Typography
            className={styles.title}
            tag="h2"
            dataTestId={`${dataTestId}-title`}
            variant="title-1-semibold">
            {title}
          </Typography>
          <Typography
            className={styles.content}
            tag="p"
            variant="body-regular"
            dataTestId={`${dataTestId}-content`}>
            {content}
          </Typography>
          <div
            data-testid={`${dataTestId}-buttons`}
            className={classNames(styles['buttons-container'], {
              [styles.vertical]: vertical,
            })}>
            {children}
          </div>
        </div>
      </div>
    );
  },
);

Alert.displayName = 'Alert';
